import React from "react";
import { MapPin, Phone, Clock } from "lucide-react";
import { pickLocale } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { useStoreLocations } from "../../hooks/useSiteContent";

/**
 * The boutiques, one card each — the same list on /contact and /about,
 * because both read the rows the admin panel edits. Renders nothing while
 * loading and nothing when no store is published, so an unfinished address
 * never reaches a customer.
 */
export default function StoreLocationsGrid({ className = "" }) {
  const { locale, t } = useI18n();
  const stores = useStoreLocations();
  if (!stores || stores.length === 0) return null;

  return (
    <section className={className} data-testid="store-locations">
      <h2 className="font-serif text-[24px] leading-snug sm:text-[28px]">
        {t("stores.title", "Mağazalarımız")}
      </h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stores.map((store) => {
          const address = pickLocale(store.address, locale);
          const hours = pickLocale(store.hours, locale);
          return (
            <div key={store.id} className="border border-[var(--adf-gray-5)] p-5" data-testid={`store-${store.id}`}>
              <div className="text-[13px] font-medium uppercase tracking-[0.12em]">
                {pickLocale(store.title, locale)}
              </div>
              {address && (
                <div className="mt-3 flex gap-2 text-[14px] leading-6 text-[var(--adf-gray-1)]">
                  <MapPin size={16} strokeWidth={1.4} className="mt-1 shrink-0 text-[var(--adf-gray-3)]" aria-hidden="true" />
                  <span>{address}</span>
                </div>
              )}
              {hours && (
                <div className="mt-2 flex gap-2 text-[14px] leading-6 text-[var(--adf-gray-1)]">
                  <Clock size={16} strokeWidth={1.4} className="mt-1 shrink-0 text-[var(--adf-gray-3)]" aria-hidden="true" />
                  <span>{hours}</span>
                </div>
              )}
              {store.phone && (
                <div className="mt-2 flex gap-2 text-[14px] leading-6">
                  <Phone size={16} strokeWidth={1.4} className="mt-1 shrink-0 text-[var(--adf-gray-3)]" aria-hidden="true" />
                  <a href={`tel:${String(store.phone).replace(/\s/g, "")}`} className="underline underline-offset-4">
                    {store.phone}
                  </a>
                </div>
              )}
              {store.map_url && (
                <a
                  href={store.map_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex min-h-[36px] items-center text-[12px] uppercase tracking-[0.12em] underline underline-offset-8 hover:text-[var(--adf-gray-2)]"
                >
                  {t("stores.map", "Xəritədə bax")}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
